import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    Users, 
    CheckCircle2, 
    XCircle, 
    AlertTriangle,
    Clock,
    RefreshCw,
    Calendar,
    Edit2,
    UserX,
    Filter,
    Download,
    FileDown,
    FileSpreadsheet
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const AttendancePage = () => {
    const { api, hasPermission, user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [todayData, setTodayData] = useState(null);
    const [attendanceList, setAttendanceList] = useState([]);
    const [services, setServices] = useState([]);
    
    // Filters
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedService, setSelectedService] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    
    // Dialog states
    const [absenceDialog, setAbsenceDialog] = useState(false);
    const [correctDialog, setCorrectDialog] = useState(false);
    const [selectedAttendance, setSelectedAttendance] = useState(null);
    
    // Form states
    const [absenceReason, setAbsenceReason] = useState('');
    const [absenceType, setAbsenceType] = useState('ABSENT');
    const [correctData, setCorrectData] = useState({ check_in_time: '', check_out_time: '', reason: '' });
    
    // Permissions
    const canValidateAbsence = hasPermission('validate_absence');
    const canCorrect = hasPermission('correct_attendance');

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            
            const [todayRes, servicesRes] = await Promise.all([
                api().get(`/attendance/today${selectedService ? `?service=${selectedService}` : ''}`),
                api().get('/services')
            ]);
            
            setTodayData(todayRes.data);
            setAttendanceList(todayRes.data.attendance || []);
            setServices(servicesRes.data.services);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    }, [api, selectedService]);

    useEffect(() => {
        fetchData();
        // Real-time polling every 10 seconds
        const interval = setInterval(fetchData, 10000);
        return () => clearInterval(interval);
    }, [fetchData]);

    const fetchByDate = async () => {
        try {
            setLoading(true);
            let url = `/attendance?date=${selectedDate}`;
            if (selectedService) url += `&service=${selectedService}`;
            if (selectedStatus) url += `&status=${selectedStatus}`;
            
            const response = await api().get(url);
            setAttendanceList(response.data);
        } catch (error) {
            toast.error('Erreur');
        } finally {
            setLoading(false);
        }
    };

    // Export functions
    const handleExportPDF = async () => {
        try {
            toast.loading('Génération du PDF...');
            let url = '/exports/attendance/pdf';
            const params = [];
            if (selectedDate) params.push(`start_date=${selectedDate}`);
            if (params.length) url += '?' + params.join('&');
            
            const response = await api().get(url, { responseType: 'blob' });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', `presence_${selectedDate || new Date().toISOString().split('T')[0]}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.dismiss();
            toast.success('PDF téléchargé');
        } catch (error) {
            toast.dismiss();
            toast.error('Erreur lors de la génération du PDF');
        }
    };

    const handleExportExcel = async () => {
        try {
            toast.loading('Génération du fichier Excel...');
            let url = '/exports/attendance/excel';
            const params = [];
            if (selectedDate) params.push(`start_date=${selectedDate}`);
            if (params.length) url += '?' + params.join('&');
            
            const response = await api().get(url, { responseType: 'blob' });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', `presence_${selectedDate || new Date().toISOString().split('T')[0]}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.dismiss();
            toast.success('Excel téléchargé');
        } catch (error) {
            toast.dismiss();
            toast.error('Erreur lors de la génération du fichier Excel');
        }
    };

    useEffect(() => {
        if (selectedDate !== new Date().toISOString().split('T')[0]) {
            fetchByDate();
        } else {
            fetchData();
        }
    }, [selectedDate, selectedStatus]);

    const handleValidateAbsence = async () => {
        if (!absenceReason.trim()) {
            toast.error('Un motif est requis');
            return;
        }
        
        try {
            await api().post(`/attendance/${selectedAttendance.id}/validate-absence`, {
                reason: absenceReason,
                absence_type: absenceType
            });
            
            toast.success('Absence validee');
            setAbsenceDialog(false);
            setAbsenceReason('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleCorrect = async () => {
        if (!correctData.reason.trim() || correctData.reason.length < 5) {
            toast.error('Un motif detaille est requis (min 5 caracteres)');
            return;
        }
        
        try {
            await api().post(`/attendance/${selectedAttendance.id}/correct`, correctData);
            toast.success('Pointage corrige');
            setCorrectDialog(false);
            setCorrectData({ check_in_time: '', check_out_time: '', reason: '' });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const openAbsenceDialog = (attendance) => {
        setSelectedAttendance(attendance);
        setAbsenceReason('');
        setAbsenceType('ABSENT');
        setAbsenceDialog(true);
    };

    const openCorrectDialog = (attendance) => {
        setSelectedAttendance(attendance);
        setCorrectData({
            check_in_time: attendance.check_in_time ? new Date(attendance.check_in_time).toISOString().slice(0, 16) : '',
            check_out_time: attendance.check_out_time ? new Date(attendance.check_out_time).toISOString().slice(0, 16) : '',
            reason: ''
        });
        setCorrectDialog(true);
    };

    const getStatusBadge = (status, autoClockOut = false, isClosed = false) => {
        const styles = {
            'PENDING': { bg: 'bg-gray-100', text: 'text-gray-600', icon: Clock },
            'PRESENT': { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2 },
            'LATE': { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: AlertTriangle },
            'ABSENT': { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
            'AUTO_ABSENT': { bg: 'bg-red-50', text: 'text-red-600', icon: XCircle },
            'NOT_CLOCKED': { bg: 'bg-orange-100', text: 'text-orange-700', icon: AlertTriangle }
        };
        const labels = {
            'PENDING': 'En attente',
            'PRESENT': 'Présent',
            'LATE': 'Retard',
            'ABSENT': 'Absent',
            'AUTO_ABSENT': 'Absent (auto)',
            'NOT_CLOCKED': 'Non pointé'
        };
        const config = styles[status] || styles['PENDING'];
        const Icon = config.icon;
        
        return (
            <div className="flex items-center gap-1">
                <Badge variant="outline" className={`${config.bg} ${config.text}`}>
                    <Icon className="w-3 h-3 mr-1" />
                    {labels[status] || status}
                </Badge>
                {autoClockOut && (
                    <Badge variant="outline" className="bg-blue-50 text-blue-600 text-xs">
                        Sortie auto
                    </Badge>
                )}
                {isClosed && (
                    <Badge variant="outline" className="bg-slate-100 text-slate-600 text-xs">
                        🔒
                    </Badge>
                )}
            </div>
        );
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    };

    const stats = todayData?.stats || {};
    const isToday = selectedDate === new Date().toISOString().split('T')[0];

    if (loading && !todayData) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="h-8 bg-muted rounded w-48"></div>
                <div className="grid grid-cols-5 gap-4">
                    {[...Array(5)].map((_, i) => <div key={i} className="h-24 bg-muted rounded-xl"></div>)}
                </div>
                <div className="h-96 bg-muted rounded-xl"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="attendance-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold text-foreground tracking-tight">
                        Presences
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Suivi des pointages en temps reel
                    </p>
                </div>
                <div className="flex gap-2">
                    {/* Export Dropdown */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" data-testid="export-attendance-btn">
                                <Download className="w-4 h-4 mr-2" />
                                Exporter
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={handleExportPDF} data-testid="export-attendance-pdf-btn">
                                <FileDown className="w-4 h-4 mr-2 text-red-500" />
                                Exporter en PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleExportExcel} data-testid="export-attendance-excel-btn">
                                <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                                Exporter en Excel
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    
                    <Button variant="outline" size="sm" onClick={fetchData}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Actualiser
                    </Button>
                </div>
            </div>

            {/* Stats Cards */}
            {isToday && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card className="bg-gradient-to-br from-accent/10 to-accent/5">
                        <CardContent className="p-4 text-center">
                            <Users className="w-6 h-6 mx-auto text-accent mb-1" />
                            <div className="text-2xl font-bold">{stats.total || 0}</div>
                            <div className="text-xs text-muted-foreground">Total</div>
                        </CardContent>
                    </Card>
                    <Card className="bg-green-50">
                        <CardContent className="p-4 text-center">
                            <CheckCircle2 className="w-6 h-6 mx-auto text-green-600 mb-1" />
                            <div className="text-2xl font-bold text-green-700">{stats.present || 0}</div>
                            <div className="text-xs text-muted-foreground">Presents</div>
                        </CardContent>
                    </Card>
                    <Card className="bg-yellow-50">
                        <CardContent className="p-4 text-center">
                            <AlertTriangle className="w-6 h-6 mx-auto text-yellow-600 mb-1" />
                            <div className="text-2xl font-bold text-yellow-700">{stats.late || 0}</div>
                            <div className="text-xs text-muted-foreground">Retards</div>
                        </CardContent>
                    </Card>
                    <Card className="bg-red-50">
                        <CardContent className="p-4 text-center">
                            <XCircle className="w-6 h-6 mx-auto text-red-600 mb-1" />
                            <div className="text-2xl font-bold text-red-700">{stats.absent || 0}</div>
                            <div className="text-xs text-muted-foreground">Absents</div>
                        </CardContent>
                    </Card>
                    <Card className="bg-gray-50">
                        <CardContent className="p-4 text-center">
                            <Clock className="w-6 h-6 mx-auto text-gray-600 mb-1" />
                            <div className="text-2xl font-bold text-gray-700">{stats.pending || 0}</div>
                            <div className="text-xs text-muted-foreground">En attente</div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm font-medium">Filtres:</span>
                        </div>
                        <Input 
                            type="date" 
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="w-40"
                        />
                        <Select value={selectedService || "all"} onValueChange={(v) => setSelectedService(v === "all" ? "" : v)}>
                            <SelectTrigger className="w-36">
                                <SelectValue placeholder="Service" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous</SelectItem>
                                {services.map(s => (
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={selectedStatus || "all"} onValueChange={(v) => setSelectedStatus(v === "all" ? "" : v)}>
                            <SelectTrigger className="w-36">
                                <SelectValue placeholder="Statut" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous</SelectItem>
                                <SelectItem value="PRESENT">Present</SelectItem>
                                <SelectItem value="LATE">Retard</SelectItem>
                                <SelectItem value="ABSENT">Absent</SelectItem>
                                <SelectItem value="NOT_CLOCKED">Non pointe</SelectItem>
                                <SelectItem value="PENDING">En attente</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Attendance Table */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-accent" />
                        {isToday ? "Pointages du jour" : `Pointages du ${new Date(selectedDate).toLocaleDateString('fr-FR')}`}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {attendanceList.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">
                            Aucun pointage pour cette date
                        </p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Employe</TableHead>
                                    <TableHead>Service</TableHead>
                                    <TableHead>Prevu</TableHead>
                                    <TableHead>Arrivee</TableHead>
                                    <TableHead>Depart</TableHead>
                                    <TableHead>Retard</TableHead>
                                    <TableHead>Statut</TableHead>
                                    <TableHead></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {attendanceList.map(att => (
                                    <TableRow key={att.id}>
                                        <TableCell className="font-medium">{att.staff_name}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className="text-xs">
                                                {att.staff_service}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="font-mono text-sm">
                                            {att.scheduled_start} - {att.scheduled_end}
                                        </TableCell>
                                        <TableCell className="font-mono">
                                            {formatTime(att.check_in_time)}
                                        </TableCell>
                                        <TableCell className="font-mono">
                                            {formatTime(att.check_out_time)}
                                        </TableCell>
                                        <TableCell>
                                            {att.late_minutes > 0 && (
                                                <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
                                                    +{att.late_minutes} min
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {getStatusBadge(att.status, att.auto_clock_out, att.is_closed)}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-1">
                                                {canValidateAbsence && (att.status === 'PENDING' || att.status === 'NOT_CLOCKED') && (
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon" 
                                                        className="h-8 w-8"
                                                        onClick={() => openAbsenceDialog(att)}
                                                        title="Declarer absent"
                                                    >
                                                        <UserX className="w-4 h-4" />
                                                    </Button>
                                                )}
                                                {canCorrect && (
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon" 
                                                        className="h-8 w-8"
                                                        onClick={() => openCorrectDialog(att)}
                                                        title="Corriger"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Validate Absence Dialog */}
            <Dialog open={absenceDialog} onOpenChange={setAbsenceDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Valider l'absence</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="p-3 bg-muted rounded-lg">
                            <p className="font-medium">{selectedAttendance?.staff_name}</p>
                            <p className="text-sm text-muted-foreground">
                                Shift: {selectedAttendance?.scheduled_start} - {selectedAttendance?.scheduled_end}
                            </p>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Type d'absence</Label>
                            <Select value={absenceType} onValueChange={setAbsenceType}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="ABSENT">Absence non justifiee</SelectItem>
                                    <SelectItem value="SICK">Maladie</SelectItem>
                                    <SelectItem value="LEAVE">Conge</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Motif *</Label>
                            <Textarea 
                                value={absenceReason}
                                onChange={(e) => setAbsenceReason(e.target.value)}
                                placeholder="Raison de l'absence..."
                                rows={3}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAbsenceDialog(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleValidateAbsence} className="bg-red-600 hover:bg-red-700">
                            Valider l'absence
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Correct Attendance Dialog */}
            <Dialog open={correctDialog} onOpenChange={setCorrectDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Corriger le pointage</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="p-3 bg-muted rounded-lg">
                            <p className="font-medium">{selectedAttendance?.staff_name}</p>
                            <p className="text-sm text-muted-foreground">
                                {selectedAttendance?.date}
                            </p>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Heure d'arrivee</Label>
                                <Input 
                                    type="datetime-local"
                                    value={correctData.check_in_time}
                                    onChange={(e) => setCorrectData({...correctData, check_in_time: e.target.value})}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Heure de depart</Label>
                                <Input 
                                    type="datetime-local"
                                    value={correctData.check_out_time}
                                    onChange={(e) => setCorrectData({...correctData, check_out_time: e.target.value})}
                                />
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Motif de la correction *</Label>
                            <Textarea 
                                value={correctData.reason}
                                onChange={(e) => setCorrectData({...correctData, reason: e.target.value})}
                                placeholder="Expliquez la raison de cette correction..."
                                rows={3}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCorrectDialog(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleCorrect}>
                            Corriger
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AttendancePage;
